import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Configuration d'axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    // Retry basique sur 429 (rate limit) avec backoff
    const config = error.config || {};
    const status = error.response?.status;
    const isGet = (config.method || 'get').toLowerCase() === 'get';
    const url = (config.url || '').toString();
    if (status === 429 && isGet && url.includes('/products')) {
      config._retryCount = config._retryCount || 0;
      if (config._retryCount < 3) {
        config._retryCount += 1;
        const base = 300; // ms
        const delay = Math.min(2000, base * Math.pow(2, config._retryCount - 1)) + Math.floor(Math.random() * 200);
        await new Promise((res) => setTimeout(res, delay));
        return api(config);
      }
    }
    return Promise.reject(error);
  }
);

// Services d'authentification
export const authService = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  verifyToken: async () => {
    const response = await api.post('/auth/verify');
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  changePassword: async (data) => {
    const response = await api.put('/auth/change-password', data);
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
};

// Services des produits
export const productService = {
  getProducts: async (params = {}, options = {}) => {
    const response = await api.get('/products', { params, ...options });
    return response.data;
  },

  getProductById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  createProduct: async (productData) => {
    const formData = new FormData();
    
    // Ajouter les données du produit
    Object.keys(productData).forEach(key => {
      if (key === 'categories' || key === 'features') {
        formData.append(key, JSON.stringify(productData[key]));
      } else if (key === 'technicalSpecs') {
        formData.append(key, JSON.stringify(productData[key]));
      } else if (key === 'stock' || key === 'pricing') {
        formData.append(key, JSON.stringify(productData[key]));
      } else if (key === 'facets') {
        // S'assurer que les facettes (dont phase, puissance, etc.) sont envoyées en JSON
        formData.append(key, JSON.stringify(productData[key] || {}));
      } else if (productData[key] !== null && productData[key] !== undefined) {
        formData.append(key, productData[key]);
      }
    });

    // Ajouter les images
    if (productData.images && productData.images.length > 0) {
      productData.images.forEach((image, index) => {
        if (image instanceof File) {
          formData.append('images', image);
        }
      });
    }

    // Ajouter les documents
    if (productData.documents) {
      Object.keys(productData.documents).forEach(key => {
        if (productData.documents[key] instanceof File) {
          formData.append(key, productData.documents[key]);
        }
      });
    }

    const response = await api.post('/products', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateProduct: async (id, productData) => {
    const formData = new FormData();
    
    // Ajouter les données du produit
    Object.keys(productData).forEach(key => {
      if (key === 'documents' || key === 'newDocuments') {
        return; // ne pas envoyer un objet documents en tant que champ normal
      }
      if (key === 'categories' || key === 'tags' || key === 'features') {
        formData.append(key, JSON.stringify(productData[key]));
      } else if (key === 'technicalSpecs') {
        formData.append(key, JSON.stringify(productData[key]));
      } else if (key === 'stock' || key === 'pricing') {
        formData.append(key, JSON.stringify(productData[key]));
      } else if (key === 'facets') {
        formData.append(key, JSON.stringify(productData[key] || {}));
      } else if (productData[key] !== null && productData[key] !== undefined) {
        formData.append(key, productData[key]);
      }
    });

    // Ajouter les images (compat: images ou newImages)
    const imagesToSend = [
      ...(productData.images || []).filter((img) => img instanceof File),
      ...(productData.newImages || []).filter((img) => img instanceof File),
    ];
    imagesToSend.forEach((image) => formData.append('images', image));

    // Ajouter les documents (compat: documents ou newDocuments)
    const docsSource = productData.documents || productData.newDocuments || {};
    Object.keys(docsSource).forEach((key) => {
      const file = docsSource[key];
      if (file instanceof File) {
        // champs attendus par le backend: datasheet, manual, certificate
        formData.append(key, file);
      }
    });

    const response = await api.put(`/products/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Toggle uniquement la visibilité (endpoint léger)
  toggleVisibility: async (id) => {
    try {
      const response = await api.patch(`/products/${id}/visibility`);
      return response.data;
    } catch (err) {
      // Fallback compat si le serveur ne supporte pas PATCH
      if (err?.response?.status === 404 || err?.response?.status === 405) {
        const resp = await api.put(`/products/${id}/visibility`);
        return resp.data;
      }
      throw err;
    }
  },

  deleteProduct: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },

  bulkDeleteProducts: async (ids = []) => {
    const response = await api.post('/products/bulk-delete', { ids });
    return response.data;
  },

  bulkDeleteAllProducts: async () => {
    const response = await api.post('/products/bulk-delete-all');
    return response.data;
  },

  searchProducts: async (query, filters = {}) => {
    const response = await api.get('/products/search', {
      params: { q: query, ...filters }
    });
    return response.data;
  },

  bulkImport: async (products) => {
    const response = await api.post('/products/bulk-import', { products });
    return response.data;
  },
};

// Services des catégories
export const categoryService = {
  getCategories: async (params = {}) => {
    const response = await api.get('/categories', { params });
    return response.data;
  },

  getCategoryById: async (id) => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },

  createCategory: async (categoryData) => {
    const formData = new FormData();
    
    Object.keys(categoryData).forEach(key => {
      if (key === 'parentCategory') {
        if (categoryData[key]) {
          formData.append(key, categoryData[key]);
        }
      } else if (key === 'specificFields') {
        formData.append(key, JSON.stringify(categoryData[key] || {}));
      } else if (key === 'categoryImage' && categoryData[key] instanceof File) {
        formData.append(key, categoryData[key]);
      } else if (categoryData[key] !== null && categoryData[key] !== undefined) {
        formData.append(key, categoryData[key]);
      }
    });

    const response = await api.post('/categories', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateCategory: async (id, categoryData) => {
    const formData = new FormData();
    
    Object.keys(categoryData).forEach(key => {
      if (key === 'parentCategory') {
        if (categoryData[key]) {
          formData.append(key, categoryData[key]);
        }
      } else if (key === 'specificFields') {
        formData.append(key, JSON.stringify(categoryData[key] || {}));
      } else if (key === 'categoryImage' && categoryData[key] instanceof File) {
        formData.append(key, categoryData[key]);
      } else if (categoryData[key] !== null && categoryData[key] !== undefined) {
        formData.append(key, categoryData[key]);
      }
    });

    const response = await api.put(`/categories/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteCategory: async (id) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },

  getCategoryStats: async () => {
    const response = await api.get('/categories/stats');
    return response.data;
  },

  reorderCategories: async (categories) => {
    const response = await api.put('/categories/reorder', { categories });
    return response.data;
  },
};

// Services des demandes de devis
export const quoteService = {
  getQuoteRequests: async (params = {}) => {
    const response = await api.get('/quotes', { params });
    return response.data;
  },

  getQuoteRequestById: async (id) => {
    const response = await api.get(`/quotes/${id}`);
    return response.data;
  },

  updateQuoteRequest: async (id, data) => {
    const response = await api.put(`/quotes/${id}`, data);
    return response.data;
  },

  deleteQuoteRequest: async (id) => {
    const response = await api.delete(`/quotes/${id}`);
    return response.data;
  },

  getQuoteStats: async () => {
    const response = await api.get('/quotes/stats');
    return response.data;
  },

  assignQuoteRequest: async (id, assignedTo) => {
    const response = await api.put(`/quotes/${id}/assign`, { assignedTo });
    return response.data;
  },

  markAsResponded: async (id) => {
    const response = await api.put(`/quotes/${id}/responded`);
    return response.data;
  },
};

// Services des paramètres (PIN d'accès admin, etc.)
export const settingsService = {
  updateAdminPin: async ({ currentPin, newPin }) => {
    const response = await api.put('/settings/admin-pin', { currentPin, newPin });
    return response.data;
  },
};

export default api;
